import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, UntypedFormControl} from "@angular/forms";
import {Subject, takeUntil} from "rxjs";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {TableColumn} from "@vex/interfaces/table-column.interface";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {LevelsService} from "@core/services/backend/common/service/levels.service";
import {fadeInUp400ms} from "@vex/animations/fade-in-up.animation";
import {stagger40ms} from "@vex/animations/stagger.animation";
import {Level} from "@core/services/backend/common/interface/levels";
import {LevelsCreateUpdateComponent} from "@app/pages/administration/levels-create-update/levels-create-update.component";
import {TableUtil} from "@vex/services/tableUtil";

@Component({
    selector: 'vex-levels',
    templateUrl: './levels.component.html',
    styleUrls: ['./levels.component.scss'],
    animations: [
        fadeInUp400ms,
        stagger40ms
    ]
})
export class LevelsComponent implements OnInit {

    layoutCtrl = new UntypedFormControl('boxed');
    private readonly destroying$ = new Subject<void>();

    levelsList: Level[] = [];
    nodeList: any[] = []; // Estructura de árbol con niveles raíz
    dataSource: MatTableDataSource<Level> | null;
    searchForm: FormGroup;
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    columns: TableColumn<Level>[] = [
        {label: 'GRIDS.ID', property: 'id', type: 'text', visible: false},
        {label: 'GRIDS.CODIGO', property: 'code', type: 'text', visible: false, cssClasses: ['font-medium']},
        {label: 'GRIDS.CODIGO_COMPLETO', property: 'fullCode', type: 'text', visible: true, cssClasses: ['font-medium', 'text-primary']},
        {label: 'GRIDS.DESCRIPCION', property: 'description', type: 'text', visible: true},
        {label: 'GRIDS.NIVEL', property: 'level', type: 'text', visible: false},
        {label: 'GRIDS.ACTIVO', property: 'active', type: 'image', visible: true},
        {label: 'GRIDS.ACCIONES', property: 'actions', type: 'button', visible: true}
    ];

    currentPage = 0;
    pageSize = 20;
    pageSizeOptions: number[] = [10, 20, 50, 100];
    isLoading: boolean = false;
    flatNodeList: Level[] = [];
    totalRecords: number = 0;
    allNodesWithChildren: Set<number> = new Set(); // Cache de qué niveles tienen hijos

    constructor(
        private levels: LevelsService,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private snackbar: MatSnackBar
    ) {
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource();
        this.searchForm = new FormGroup({
            code: new FormControl(),
            description: new FormControl(),
            level: new FormControl()
        });
        this.loadData();
    }

    loadData() {
        const that = this;
        this.isLoading = true;
        this.nodeList = []; // Limpiar lista anterior
        this.initializeData().then(() => {
            // Actualizar la lista plana basada en los nodos raíz
            this.updateFlatNodeList();
            this.isLoading = false;
        });
    }

    private getSearch() {
        return {
            code: this.searchForm.get("code")?.value ? '%' + this.searchForm.get("code")?.value.toString().toUpperCase() + '%' : '',
            description: this.searchForm.get("description")?.value ? '%' + this.searchForm.get("description")?.value.toString().toUpperCase() + '%' : '',
            level: this.searchForm.get("level")?.value ? this.searchForm.get("level")?.value : 0
            // No incluir parent_id aquí, lo filtraremos en el frontend
        };
    }

    async initializeData() {
        const that = this;
        return await new Promise<void>((resolve, reject) => {
            // Primero verificar qué niveles tienen hijos (necesitamos todos los niveles para esto)
            this.verifyAllNodesWithChildren().then(() => {
                // Luego cargar niveles (filtrar solo raíz en el frontend)
                const searchObj = that.getSearch();
                const list = this.levels.list(JSON.stringify(searchObj), 0, 10000, 'level ASC, code ASC');
            list.pipe(takeUntil(this.destroying$)).subscribe({
                next: (data: any) => {
                        const allLevels = data.data || data || [];
                        // Filtrar solo niveles raíz (sin padre) y normalizar
                        that.nodeList = allLevels
                            .filter((level: any) => {
                                // Solo incluir niveles sin padre
                                const parentId = level.parent_id || (level as any).ParentID || null;
                                return parentId == null;
                            })
                            .map((level: any) => {
                                const levelId = level.id || (level as any).ID;
                                const normalized: any = {
                                    ...level,
                                    id: levelId,
                                    parent_id: null,
                                    parentId: null,
                                    expanded: false,
                                    hasChildren: false,
                                    children: [],
                                    isLoading: false
                                };
                                // Verificar si tiene hijos usando el resultado de verifyAllNodesWithChildren
                                normalized.hasChildren = that.allNodesWithChildren?.has(levelId) || false;
                                return normalized;
                            });
                        
                    that.paginator.pageIndex = that.currentPage;
                        // El total de registros es solo los niveles raíz
                        that.totalRecords = that.nodeList.length;
                        that.paginator.length = that.totalRecords;
                    resolve();
                },
                error: (err) => {
                    console.error('Error loading levels:', err);
                        that.nodeList = [];
                        that.flatNodeList = [];
                        that.totalRecords = 0;
                    resolve();
                }
                });
            });
        });
    }

    pageChanged(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex;
        this.loadData();
    }

    trackByProperty<T>(index: number, column: TableColumn<T>) {
        return column.property;
    }

    get visibleColumns() {
        return this.columns.filter(column => column.visible).map(column => column.property);
    }

    buscar() {
        this.currentPage = 0;
        this.loadData();
    }

    createLevel() {
        this.dialog.open(LevelsCreateUpdateComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    updateLevel(level: Level) {
        // Normalizar el ID (puede venir como ID en mayúscula del backend)
        const levelId = (level as any).ID || level.id;
        // Normalizar el parent_id
        const parentId = level.parent_id || (level as any).ParentID || (level as any).parentId || null;
        
        // Crear una copia del objeto para asegurar que todas las propiedades estén presentes
        const levelData: Level = {
            id: levelId,
            code: level.code || '',
            description: level.description || '',
            level: level.level || 1,
            parent_id: parentId,
            active: level.active !== false
        };
        
        this.dialog.open(LevelsCreateUpdateComponent, {
            data: levelData,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    deleteLevel(level: Level) {
        const that = this;
        // Normalizar el ID (puede venir como ID en mayúscula del backend)
        const levelId = (level as any).ID || level.id;
        
        if (!levelId) {
            that.snackbar.open('Error: ID del nivel no válido', '', {
                duration: 3000
            });
            return;
        }
        
        // Crear un objeto con el ID correcto para el delete
        const levelToDelete: Level = {
            ...level,
            id: levelId
        };
        
        this.levels.delete(levelToDelete).subscribe({
            next(result) {
                that.snackbar.open('Nivel eliminado correctamente', '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                that.snackbar.open('Error al eliminar el nivel', '', {
                    duration: 3000
                });
            }
        });
    }

    /**
     * Verifica qué niveles tienen hijos consultando todos los niveles del sistema
     */
    private async verifyAllNodesWithChildren(): Promise<void> {
        return new Promise<void>((resolve) => {
            // Cargar todos los niveles para verificar cuáles tienen hijos
            this.levels.list('', 0, 10000, 'id ASC').pipe(takeUntil(this.destroying$)).subscribe({
                next: (data: any) => {
                    const allLevels = data.data || data || [];
                    const parentIds = new Set<number>();
                    
                    // Recopilar todos los parent_id
                    allLevels.forEach((level: any) => {
                        const parentId = level.parent_id || (level as any).ParentID;
                        if (parentId != null) {
                            parentIds.add(parentId);
                        }
                    });
                    
                    // Guardar en cache qué niveles tienen hijos
                    this.allNodesWithChildren = parentIds;
                    resolve();
                },
                error: (err) => {
                    console.error('Error verificando niveles con hijos:', err);
                    this.allNodesWithChildren = new Set();
                    resolve();
                }
            });
        });
    }

    /**
     * Actualiza la lista plana de nodos basándose en los nodos expandidos
     */
    private updateFlatNodeList() {
        this.flatNodeList = [];
        this.flattenTree(this.nodeList);
        this.dataSource.data = [...this.flatNodeList];
    }

    /**
     * Aplana el árbol de nodos, incluyendo solo los nodos expandidos y sus hijos
     */
    private flattenTree(nodes: any[]) {
        for (const node of nodes) {
            this.flatNodeList.push(node);
            // Solo incluir hijos si el nodo está expandido
            if (node.expanded && node.children && node.children.length > 0) {
                this.flattenTree(node.children);
            }
        }
    }

    /**
     * Obtiene la indentación basada en la profundidad del nodo en el árbol
     */
    getIndentation(row: any): string {
        // Calcular la profundidad contando los niveles de parent_id
        let depth = 0;
        let current = row;
        while (current && current.parentId != null) {
            depth++;
            // Buscar el padre en la estructura de árbol
            current = this.findNodeById(current.parentId);
        }
        return `${depth * 20}px`;
    }

    /**
     * Busca un nodo por ID en toda la estructura de árbol
     */
    private findNodeById(id: number, nodes?: any[]): any {
        if (!nodes) {
            nodes = this.nodeList;
        }
        for (const node of nodes) {
            if (node.id === id) {
                return node;
            }
            if (node.children && node.children.length > 0) {
                const found = this.findNodeById(id, node.children);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    /**
     * Calcula el código completo basado en la jerarquía
     * Ejemplo: Si el padre tiene código completo "1" y el hijo tiene código "2", retorna "1.2"
     * Si el padre tiene código completo "1.1" y el hijo tiene código "3", retorna "1.1.3"
     */
    getFullCode(row: any): string {
        if (!row) {
            return '';
        }
        
        // Si no tiene padre, el código completo es solo su código
        if (!row.parentId && row.parentId !== 0) {
            return row.code || '';
        }
        
        // Buscar el padre en toda la estructura de árbol (incluyendo hijos cargados)
        const parent = this.findNodeById(row.parentId);
        if (!parent) {
            // Si no se encuentra el padre, retornar solo el código actual
            // Esto puede pasar si el padre no está cargado aún
            return row.code || '';
        }
        
        // Obtener el código completo del padre recursivamente
        const parentFullCode = this.getFullCode(parent);
        
        // Si el padre tiene código completo, concatenar con punto
        if (parentFullCode) {
            return `${parentFullCode}.${row.code || ''}`;
        } else {
            // Si el padre no tiene código completo, usar solo el código del padre
            const parentCode = parent.code || '';
            if (parentCode) {
                return `${parentCode}.${row.code || ''}`;
            } else {
                return row.code || '';
            }
        }
    }

    /**
     * Verifica si un nodo tiene hijos
     */
    hasChildren(row: any): boolean {
        return (row as any).hasChildren === true;
    }

    /**
     * Alterna el estado de expansión de un nodo
     */
    toggleNode(row: any) {
        // Si se está expandiendo el nodo
        if (!row.expanded) {
            // Si no tiene hijos cargados, cargarlos desde el backend
            if (!row.children || row.children.length === 0) {
                // Cargar hijos antes de expandir
                this.loadChildrenForNode(row, () => {
                    // Después de cargar, expandir solo si tiene hijos
                    if (row.children && row.children.length > 0) {
                        row.expanded = true;
                        row.hasChildren = true;
                        this.updateFlatNodeList();
                    } else {
                        row.hasChildren = false;
                    }
                });
            } else {
                // Si ya tiene hijos cargados, solo expandir
                row.expanded = true;
                this.updateFlatNodeList();
            }
        } else {
            // Si se está colapsando, cambiar el estado
            row.expanded = false;
            this.updateFlatNodeList();
        }
    }

    /**
     * Carga los hijos de un nodo desde el backend
     */
    private loadChildrenForNode(node: any, callback?: () => void): void {
        if (!node.id) {
            return;
        }

        node.isLoading = true;

        // Buscar niveles cuyo parent_id sea el id del nodo actual
        const searchObj = { parent_id: node.id };
        const searchString = JSON.stringify(searchObj);

        this.levels.list(searchString, 0, 10000, 'level ASC, code ASC').pipe(takeUntil(this.destroying$)).subscribe({
            next: (data: any) => {
                const allLevels = data.data || data || [];
                
                // Filtrar SOLO los hijos directos del nodo actual
                const childrenLevels = allLevels.filter((level: any) => {
                    const levelParentId = level.parent_id || (level as any).ParentID;
                    return levelParentId != null && levelParentId === node.id;
                });

                // Mapear los niveles a la estructura de nodo
                const children = childrenLevels.map((level: any) => {
                    const levelId = level.id || (level as any).ID;
                    const normalized: any = {
                        ...level,
                        id: levelId,
                        parent_id: node.id,
                        parentId: node.id,
                        expanded: false,
                        hasChildren: this.allNodesWithChildren.has(levelId),
                        children: [],
                        isLoading: false
                    };
                    return normalized;
                });

                // Asignar los hijos al nodo
                node.children = children;
                node.hasChildren = children.length > 0;
                node.isLoading = false;
                
                // Ejecutar callback si existe
                if (callback) {
                    callback();
                } else {
                    this.updateFlatNodeList();
                }
            },
            error: (err) => {
                console.error('Error cargando hijos del nodo:', err);
                node.isLoading = false;
                node.hasChildren = false;
                node.children = [];
                if (callback) {
                    callback();
                }
            }
        });
    }

    /**
     * Expande todos los nodos
     */
    expandAll() {
        const expandNode = (node: any) => {
            if (node.hasChildren) {
                if (!node.children || node.children.length === 0) {
                    // Cargar hijos primero
                    this.loadChildrenForNode(node, () => {
                        node.expanded = true;
                        if (node.children) {
                            node.children.forEach((child: any) => expandNode(child));
                        }
                        this.updateFlatNodeList();
                    });
                } else {
                    node.expanded = true;
                    node.children.forEach((child: any) => expandNode(child));
                }
            }
        };
        
        this.nodeList.forEach((node: any) => expandNode(node));
        this.updateFlatNodeList();
    }

    /**
     * Contrae todos los nodos
     */
    collapseAll() {
        const collapseNode = (node: any) => {
            node.expanded = false;
            if (node.children) {
                node.children.forEach((child: any) => collapseNode(child));
            }
        };
        
        this.nodeList.forEach((node: any) => collapseNode(node));
        this.updateFlatNodeList();
    }

    exportToExcel() {
        this.isLoading = true;
        this.levels.list('', 0, 10000, 'level ASC, code ASC').pipe(takeUntil(this.destroying$)).subscribe({
            next: (data: any) => {
                const allLevels: any[] = data.data || data || [];
                
                const levelsById = new Map<number, any>();
                allLevels.forEach((lv: any) => {
                    const id = lv.id || (lv as any).ID;
                    levelsById.set(id, lv);
                });

                const getFullCodeForLevel = (lv: any): string => {
                    const parentId = lv.parent_id || (lv as any).ParentID;
                    const code = lv.code || '';
                    if (!parentId) return code;
                    const parent = levelsById.get(parentId);
                    if (!parent) return code;
                    const parentFullCode = getFullCodeForLevel(parent);
                    return parentFullCode ? `${parentFullCode}.${code}` : code;
                };

                const exportData = allLevels.map((lv: any) => {
                    const id = lv.id || (lv as any).ID;
                    const parentId = lv.parent_id || (lv as any).ParentID || '';
                    const parent = parentId ? levelsById.get(parentId) : null;

                    return {
                        'ID': id || '',
                        'Código': lv.code || '',
                        'Código Completo': getFullCodeForLevel(lv),
                        'Descripción': lv.description || '',
                        'Nivel': lv.level || '',
                        'ID Padre': parentId || '',
                        'Código Padre': parent ? (parent.code || '') : '',
                        'Descripción Padre': parent ? (parent.description || '') : '',
                        'Activo': lv.active !== false ? 'Sí' : 'No'
                    };
                });

                TableUtil.exportArrayToExcel(exportData, 'Niveles');
                this.isLoading = false;
            },
            error: (err) => {
                console.error('Error al exportar niveles:', err);
                this.snackbar.open('Error al exportar los niveles', '', { duration: 3000 });
                this.isLoading = false;
            }
        });
    }
}

